export type LegacyRecord = Record<string, unknown>;

export interface LegacyRecordListResponse {
  data?: LegacyRecord[];
}

export interface LegacyRecordResponse {
  data?: LegacyRecord;
}

export interface MyAccountSummary {
  account?: {
    id: string;
    name: string;
    legacyCustomerId?: string | null;
  };
  services?: {
    count?: number;
  };
  tickets?: {
    openCount?: number;
    recentClosedCount?: number;
  };
  billing?: {
    totalBalance?: number | null;
    currentBalance?: number | null;
    pastDue30?: number | null;
    pastDue60?: number | null;
    pastDue90?: number | null;
  };
  lastPayment?: {
    amount?: number | null;
    postedAt?: string | null;
    method?: string | null;
  };
}

export interface MyAccountTicketDetail {
  ticket?: LegacyRecord;
  notes?: LegacyRecord[];
  contacts?: LegacyRecord[];
  service?: LegacyRecord | null;
}

export interface MyAccountPaymentRequest {
  amount: number;
  cardName: string;
  cardNumber: string;
  cardExpMonth: string;
  cardExpYear: string;
  cardCvv: string;
  billingAddress: string;
  billingPostalCode: string;
  paymentTypeId?: number;
  transactionNote?: string;
  contactId?: number;
}

export interface MyAccountPaymentResponse {
  status?: string;
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers ?? {})
    },
    ...options
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed: ${response.status}`);
  }

  const raw = await response.text();
  if (!raw.trim()) {
    return {} as T;
  }
  return JSON.parse(raw) as T;
}

export async function fetchMyAccountSummary(accountId?: string): Promise<MyAccountSummary> {
  const url = accountId
    ? `/api/v1/my-account/summary?accountId=${encodeURIComponent(accountId)}`
    : '/api/v1/my-account/summary';
  return fetchJSON<MyAccountSummary>(url);
}

export async function listMyAccountServices(accountId?: string): Promise<LegacyRecord[]> {
  const url = accountId
    ? `/api/v1/my-account/services?accountId=${encodeURIComponent(accountId)}`
    : '/api/v1/my-account/services';
  const payload = await fetchJSON<LegacyRecordListResponse>(url);
  return payload.data ?? [];
}

export async function listMyAccountTickets(params?: {
  accountId?: string;
  status?: 'open' | 'closed' | 'all';
  includeClosedDays?: number;
  serviceId?: number;
}): Promise<LegacyRecord[]> {
  const query = new URLSearchParams();
  if (params?.accountId) query.set('accountId', params.accountId);
  if (params?.status) query.set('status', params.status);
  if (typeof params?.includeClosedDays === 'number') query.set('includeClosedDays', String(params.includeClosedDays));
  if (typeof params?.serviceId === 'number') query.set('serviceId', String(params.serviceId));

  const url = query.toString() ? `/api/v1/my-account/tickets?${query.toString()}` : '/api/v1/my-account/tickets';
  const payload = await fetchJSON<LegacyRecordListResponse>(url);
  return payload.data ?? [];
}

export async function getMyAccountTicket(ticketId: number, accountId?: string): Promise<MyAccountTicketDetail> {
  const url = accountId
    ? `/api/v1/my-account/tickets/${ticketId}?accountId=${encodeURIComponent(accountId)}`
    : `/api/v1/my-account/tickets/${ticketId}`;
  return fetchJSON<MyAccountTicketDetail>(url);
}

export async function getMyAccountBillingSummary(accountId?: string): Promise<LegacyRecord | null> {
  const url = accountId
    ? `/api/v1/my-account/billing/summary?accountId=${encodeURIComponent(accountId)}`
    : '/api/v1/my-account/billing/summary';
  const payload = await fetchJSON<LegacyRecordResponse>(url);
  return payload.data ?? null;
}

export async function listMyAccountBillingHistory(accountId?: string, limit?: number): Promise<LegacyRecord[]> {
  const query = new URLSearchParams();
  if (accountId) query.set('accountId', accountId);
  if (typeof limit === 'number') query.set('limit', String(limit));
  const url = query.size ? `/api/v1/my-account/billing/history?${query.toString()}` : '/api/v1/my-account/billing/history';
  const payload = await fetchJSON<LegacyRecordListResponse>(url);
  return payload.data ?? [];
}

export async function submitMyAccountPayment(
  payload: MyAccountPaymentRequest,
  accountId?: string
): Promise<MyAccountPaymentResponse> {
  const url = accountId
    ? `/api/v1/my-account/payments?accountId=${encodeURIComponent(accountId)}`
    : '/api/v1/my-account/payments';
  return fetchJSON<MyAccountPaymentResponse>(url, {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

function getLegacyValue(record: LegacyRecord, keys: string[]): unknown {
  const lookup = new Map<string, unknown>();
  Object.entries(record).forEach(([key, value]) => {
    lookup.set(key.toLowerCase(), value);
  });

  for (const key of keys) {
    const value = lookup.get(key.toLowerCase());
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

export function getLegacyString(record: LegacyRecord, keys: string[]): string | null {
  const value = getLegacyValue(record, keys);
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number') return String(value);
  if (value instanceof Date) return value.toISOString();
  return null;
}

export function getLegacyNumber(record: LegacyRecord, keys: string[]): number | null {
  const value = getLegacyValue(record, keys);
  if (value === undefined || value === null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function getLegacyDate(record: LegacyRecord, keys: string[]): Date | null {
  const value = getLegacyValue(record, keys);
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

export function formatCurrency(value?: number | null): string {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

export function formatDate(value?: Date | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(value);
}

export function formatDateTime(value?: Date | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(value);
}
